import React, { useState } from 'react'
import copy from 'copy-to-clipboard'
import classNames from 'classnames'
import { Translate } from 'utils/localize'
import { ParagraphRegular } from 'layouts/typography'
import getConfig from 'config/web'
import { Row, Column, Icon } from 'layouts'
import SocialIcon from 'features/social-icon/components'
import SectionType from 'models/types/ts/sectionType'
import style from '../../RecruitAuthenticated.module.scss'

const { webBaseUrl, shareFacebook, shareTwitter, shareReddit } = getConfig()

interface Props {
    section: SectionType
    invitationToken: string
    isFetchingInvitation: boolean
    language: string
}

const SocialShare = ({
    section,
    invitationToken,
    isFetchingInvitation,
    language,
}: Props): JSX.Element => {
    const [linkCopied, setLinkCopied] = useState(false)

    const getShareLink = (): string => {
        let langPrefix = ''
        if (language !== 'en') {
            langPrefix = `/${language}`
        }

        return `${webBaseUrl}${langPrefix}/signup?invc=${invitationToken}`
    }

    const copyToClipBoard = (): void => {
        if (!isFetchingInvitation) {
            copy(getShareLink(), {
                format: 'text/plain',
            })
            setLinkCopied(true)

            setTimeout(() => {
                if (linkCopied) {
                    setLinkCopied(false)
                }
            }, 2000)
        }
    }

    const facebookShareLink = shareFacebook + getShareLink()
    const twitterShareLink = shareTwitter + getShareLink()
    const redditShareLink = shareReddit + getShareLink()
    const copiedClassName = classNames(style.copied, {
        [style.active]: linkCopied,
    })

    return (
        <Translate>
            {({ translate }) => (
                <div className={style.intro}>
                    <Row className={style.socialShareWrapper}>
                        <Column xs={12} className={style.intro}>
                            <ParagraphRegular>
                                {section?.headline}
                            </ParagraphRegular>
                        </Column>
                        <Column xs={12} className={style.header}>
                            <div className={style.hexagon}>
                                <div className={style.innerHex}>
                                    <Icon solid name="share-alt" />
                                </div>
                            </div>
                            <h4>{section?.teaser}</h4>
                        </Column>
                        <Column sm={8} xs={12} className={style.socialShare}>
                            <div className={style.link}>
                                <input
                                    name="recruitmentUrl"
                                    readOnly
                                    disabled={isFetchingInvitation}
                                    type="text"
                                    value={
                                        isFetchingInvitation
                                            ? 'Loading...'
                                            : getShareLink()
                                    }
                                    onFocus={(e) => e.target.select()}
                                />
                                <div
                                    role="none"
                                    className={style.copy}
                                    onClick={() => copyToClipBoard()}
                                >
                                    <Icon light name="copy" />
                                </div>
                            </div>
                        </Column>
                        <Column sm={4} xs={12}>
                            <div className={style.socialShareList}>
                                <SocialIcon
                                    id="facebook"
                                    title={translate(
                                        'recruit.shareLinkOnFacebook'
                                    ).toString()}
                                    href={facebookShareLink}
                                    name="facebook-f"
                                    showBrandColor
                                />
                                <SocialIcon
                                    id="twitter"
                                    title={translate(
                                        'recruit.shareLinkOnTwitter'
                                    ).toString()}
                                    href={twitterShareLink}
                                    name="twitter"
                                    showBrandColor
                                />
                                <SocialIcon
                                    id="reddit"
                                    title={translate(
                                        'recruit.shareLinkOnReddit'
                                    ).toString()}
                                    href={redditShareLink}
                                    name="reddit"
                                    showBrandColor
                                />
                            </div>
                        </Column>
                        <Column md={8}>
                            <div className={copiedClassName}>
                                <Translate id="recruit.linkCopied">
                                    Link copied
                                </Translate>
                            </div>
                        </Column>
                    </Row>
                </div>
            )}
        </Translate>
    )
}

export default SocialShare
